// Shared dynamic colour-pool used by the organisation-group comparison charts
// (response_times, sleep_data, support_entries). Colours are assigned only
// when a comparator is ticked and returned to the pool when un-ticked, so the
// 16-colour palette comfortably covers a realistic ~10 simultaneous selections
// without clashing — even when the underlying group has 100+ orgs and floors.

const COLOR_POOL = [
  '#146ff7',
  '#dc3545',
  '#10b981',
  '#8b5cf6',
  '#f59e0b',
  '#06b6d4',
  '#ec8333',
  '#84cc16',
  '#ec4899',
  '#6366f1',
  '#14b8a6',
  '#a855f7',
  '#0ea5e9',
  '#c026d3',
  '#65a30d',
  '#fb7185',
];

// Fallback used once the pool is exhausted. Realistic max is ~10, so this
// only kicks in if a group somehow has 17+ comparators ticked at once.
export const FALLBACK_COLOR = '#94a3b8';

export class ComparatorColorPool {
  constructor() {
    this.assignments = new Map();
    this.usedIndices = new Set();
  }

  // Each compareId hashes to a preferred slot so the same entity gets the
  // same colour across reloads, regardless of selection order or which other
  // comparators are currently ticked. If the preferred slot is already taken
  // (hash collision within a render), we fall through to the next free slot.
  assign(compareId) {
    if (this.assignments.has(compareId)) return this.assignments.get(compareId);

    const preferred = this.preferredIndex(compareId);
    let index = this.usedIndices.has(preferred)
      ? this.firstFreeIndex()
      : preferred;

    if (index === -1) {
      this.assignments.set(compareId, FALLBACK_COLOR);
      return FALLBACK_COLOR;
    }

    this.usedIndices.add(index);
    const color = COLOR_POOL[index];
    this.assignments.set(compareId, color);
    return color;
  }

  release(compareId) {
    const color = this.assignments.get(compareId);
    if (!color) return;
    this.assignments.delete(compareId);
    if (color === FALLBACK_COLOR) return;
    const index = COLOR_POOL.indexOf(color);
    if (index !== -1) this.usedIndices.delete(index);
  }

  get(compareId) {
    return this.assignments.get(compareId);
  }

  preferredIndex(compareId) {
    let hash = 0;
    for (let i = 0; i < compareId.length; i++) {
      hash = (hash * 31 + compareId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % COLOR_POOL.length;
  }

  firstFreeIndex() {
    for (let i = 0; i < COLOR_POOL.length; i++) {
      if (!this.usedIndices.has(i)) return i;
    }
    return -1;
  }
}
